import { createSlice } from '@reduxjs/toolkit';
import * as clientService from '../services/clients';
import * as temporaryLicensesService from '../services/temporaryLicenses';
import * as offlineLicensesService from '../services/offlineLicenses';
import * as permanentLicensesService from '../services/permanentLicenses';
import * as productReleasesService from '../services/productReleases';
import * as accountsService from '../services/accounts';
import * as webViewerServersService from '../services/webViewerServers';
import * as webViewerLicensesService from '../services/webViewerLicenses';
import * as trialRejectedDevicesService from '../services/trialRejectedDevices';
import * as trialSettingsService from '../services/trialSettings'
import * as banService from '../services/ban';

export const objectHistoryNames = {
  clients: 'clients',
  temporaryLicenses: 'temporaryLicenses',
  permanentLicenses: 'permanentLicenses',
  offlineLicenses: 'offlineLicenses',
  productReleases: 'productReleases',
  accounts: 'accounts',
  webViewerServers: 'webViewerServers',
  webViewerLicenses: 'webViewerLicenses',
  trialRejectedDevices: 'trialRejectedDevices',
  trialSettings: 'trialSettings',
  banHistory: 'banHistory',
}

const objectToServiceMapping = {
	[objectHistoryNames.clients]: {
		getHistory: clientService.getClientsHistory,
		getHistoryById: clientService.getClientsHistoryById,
	},
	[objectHistoryNames.temporaryLicenses]: {
		getHistory: temporaryLicensesService.getTemporaryLicenseHistory,
		getHistoryById: temporaryLicensesService.getTemporaryLicenseHistoryById,
	},
	[objectHistoryNames.permanentLicenses]: {
		getHistory: permanentLicensesService.getPermanentLicenseHistory,
		getHistoryById: permanentLicensesService.getPermanentLicenseHistoryById,
	},
	[objectHistoryNames.offlineLicenses]: {
		getHistory: offlineLicensesService.getOfflineLicenseHistory,
		getHistoryById: offlineLicensesService.getOfflineLicenseHistoryById,
	},
	[objectHistoryNames.productReleases]: {
		getHistory: productReleasesService.getProductReleaseHistory,
		getHistoryById: productReleasesService.getProductReleaseHistoryById,
	},
	[objectHistoryNames.accounts]: {
		getHistory: accountsService.getAccountsHistory,
		getHistoryById: accountsService.getAccountsHistoryById,
	},
	[objectHistoryNames.webViewerServers]: {
		getHistory: webViewerServersService.getWebViewerServerHistory,
		getHistoryById: webViewerServersService.getWebViewerServerHistoryById,
	},
	[objectHistoryNames.webViewerLicenses]: {
		getHistory: webViewerLicensesService.getWebViewerLicenseHistory,
		getHistoryById: webViewerLicensesService.getWebViewerLicenseHistoryById,
	},
	[objectHistoryNames.trialRejectedDevices]: {
		getHistory: trialRejectedDevicesService.getTrialRejectedDeviceHistory,
		getHistoryById: trialRejectedDevicesService.getTrialRejectedDeviceHistoryById,
	},
	[objectHistoryNames.trialSettings]: {
		getHistory: trialSettingsService.getTrialSettingsHistory,
		getHistoryById: trialSettingsService.getTrialSettingsHistoryById,
	},
  [objectHistoryNames.banHistory]: {
		getHistory: banService.getBannedDeviceHistory,
		getHistoryById: banService.getBannedDeviceHistoryById,
	},
};

const getEditedFields = (historyRecords, objectName = null) => {
  const resultData = [];
  if (historyRecords) {  
    historyRecords.forEach((historyRecord) => {
      switch (objectName)
      {
        case objectHistoryNames.permanentLicenses:
          if (historyRecord.previousSupportDayCount && !resultData.includes('supportDayCount'))
            resultData.push('supportDayCount');
          break;

        case objectHistoryNames.temporaryLicenses:
          if (historyRecord.previousValidityTerm && !resultData.includes('term'))
            resultData.push('term');
          break;

        default:
          if (!historyRecord.previousValues)
            break;
          Object.keys(historyRecord.previousValues).forEach((editedField) => {
            if (!resultData.includes(editedField))
              resultData.push(editedField);
          })};
  })}
    return resultData;
}

const sliceName = 'objectHistory';

const initialState = Object.keys(objectToServiceMapping).reduce((acc, i) => ({
  ...acc,
  [i]: {
    data: [],
    isFetching: false,
    error: null,
  }
}), {});

const objectHistory = createSlice({
  name: sliceName,
  initialState,
  reducers: {
    historyFetching: (state, action) => {
      const { objectName } = action.payload;
      state[objectName].isFetching = true;
      state[objectName].error = null;
    },
    historyFetched: (state, action) => {
      const { history, objectName } = action.payload;
      state[objectName].isFetching = false;
      state[objectName].data = history;
    },
    historyError: (state, action) => {
      const { error, objectName } = action.payload;
      state[objectName].isFetching = false;
      state[objectName].error = error;
    },
  },
});

const { reducer, actions } = objectHistory;

const {
  historyFetching, historyFetched, historyError,
} = actions;

export const fetchHistory = (id, objectName) => (dispatch) => {
  dispatch(historyFetching({ objectName }));
  return objectToServiceMapping[objectName]
    .getHistory(id)
    .then((history) => dispatch(historyFetched({ history, objectName })))
    .catch((error) => {
      error.clientMessage = `Can't get ${objectName} history`;
      dispatch(historyError({ error, objectName }));
    });
};

export const fetchHistoryById = (localId, objectName) => (dispatch) => {
  dispatch(historyFetching({ objectName }));
  return objectToServiceMapping[objectName]
    .getHistoryById(localId)
    .then((history) => dispatch(historyFetched({ history, objectName })))
    .catch((error) => {
      error.clientMessage = `Can't get ${objectName} history`;
      dispatch(historyError({ error, objectName }))
    });
};


export const objectHistorySelectors = {
  objectHistoryState: (state) => state[sliceName],

  clients: (state) => state[sliceName][objectHistoryNames.clients],
  clientsEditedFields: (state) => getEditedFields(state[sliceName][objectHistoryNames.clients].data, objectHistoryNames.clients),

  temporaryLicenses: (state) => state[sliceName][objectHistoryNames.temporaryLicenses],
  temporaryLicensesEditedFields: (state) => getEditedFields(state[sliceName][objectHistoryNames.temporaryLicenses].data, objectHistoryNames.temporaryLicenses),

  permanentLicenses: (state) => state[sliceName][objectHistoryNames.permanentLicenses],
  permanentLicensesEditedFields: (state) => getEditedFields(state[sliceName][objectHistoryNames.permanentLicenses].data, objectHistoryNames.permanentLicenses),

  offlineLicenses: (state) => state[sliceName][objectHistoryNames.offlineLicenses],
  offlineLicensesEditedFields: (state) => getEditedFields(state[sliceName][objectHistoryNames.offlineLicenses].data, objectHistoryNames.offlineLicenses),

  productReleases: (state) => state[sliceName][objectHistoryNames.productReleases],
  productReleasesEditedFields: (state) => getEditedFields(state[sliceName][objectHistoryNames.productReleases].data, objectHistoryNames.productReleases),
  
  accounts: (state) => state[sliceName][objectHistoryNames.accounts],

  webViewerServers: (state) => state[sliceName][objectHistoryNames.webViewerServers],
  webViewerServersEditedFields: (state) => getEditedFields(state[sliceName][objectHistoryNames.webViewerServers].data, objectHistoryNames.webViewerServers),

  webViewerLicenses: (state) => state[sliceName][objectHistoryNames.webViewerLicenses],
  webViewerLicensesEditedFields: (state) => getEditedFields(state[sliceName][objectHistoryNames.webViewerLicenses].data, objectHistoryNames.webViewerLicenses),

  trialRejectedDevices: (state) => state[sliceName][objectHistoryNames.trialRejectedDevices],
  trialRejectedDevicesEditedFields: (state) => getEditedFields(state[sliceName][objectHistoryNames.trialRejectedDevices].data, objectHistoryNames.trialRejectedDevices),

  trialSettings: (state) => state[sliceName][objectHistoryNames.trialSettings],
  trialSettingsEditedFields: (state) => getEditedFields(state[sliceName][objectHistoryNames.trialSettings].data, objectHistoryNames.trialSettings),

  banHistory: (state) => state[sliceName][objectHistoryNames.banHistory],
  banHistoryEditedFields: (state) => getEditedFields(state[sliceName][objectHistoryNames.banHistory].data, objectHistoryNames.banHistory),
}

export default reducer;



// WEBPACK FOOTER //
// ./src/store/objectHistorySlice.js